import {
  AMCAdditionalFieldFilter,
  AMCReportColumns,
  PrunedAMCReportType,
  VisualizationType,
} from "~/typedef/amc/reportType";
import {
  AMCAdditionalFieldValues,
  AMCReportConfig,
  PrunedAMCScheduledReport,
} from "~/typedef/amc/scheduledReport";
import {
  AMCCSVRows,
  AMCGenericReportTabularResponse,
} from "~/typedef/amc/amcReport";
import { Campaign, PrunedCampaignGroup } from "~/typedef/amc/campaignGroups";
import { TAG_TYPES, api } from "@store/apiSlice";

import { INTERVAL } from "~/store/utils/dateTimeUtils";
import { PaginationArgs } from "~/typedef/pagination";
import { PrunedAMCScheduledReportRun } from "~/typedef/amc/scheduledReportRun";
import { baseUrl } from "~/configs";
import { globalQueryErrorHandler } from "@store/utils/errorHandlerUtils";
import { setInfoMessage } from "../globalToast.redux";

export enum AMCReportTypeSelect {
  ALL = "ALL",
  STANDARD = "STANDARD",
  CUSTOM = "CUSTOM",
}

interface FetchReportTypesArgs {
  activeOnly?: boolean;
  searchText?: string;
  filterType?: AMCReportTypeSelect;
}

interface FetchReportTypesResponse {
  reportTypes: PrunedAMCReportType[];
}

interface FetchScheduledReportsArgs {
  mid: string;
  queryType?: string;
  status?: string;
  paginationParams: PaginationArgs;
}

interface FetchScheduledReportsResponse {
  scheduledReports: PrunedAMCScheduledReport[];
  total: number;
}

export interface CreateScheduledReportArgs {
  mid: string;
  queryType: string;
  reportName: string;
  reportConfig: Omit<
    AMCReportConfig,
    "fromDate" | "toDate" | "priorFromDate" | "priorToDate"
  > & {
    fromDate?: number;
    toDate?: number;
    priorFromDate?: number;
    priorToDate?: number;
  };
  description?: string;
  reportRecipients: string[];
  parameterValues?: AMCAdditionalFieldValues;
  successMsg: string;
}

interface CreateScheduledReportResponse {}

interface UpdateReportCustomPromptArgs {
  reportRunId: string;
  customPrompt: string;
  successMsg: string;
}

interface FetchReportRunArgs {
  paginationParams: PaginationArgs;
  searchParams: {
    filterValue?: string[];
    filterKey?: string;
  }[];
  currentPeriodPrimaryKeyValues?: string[];
  reportRunId: string;
}

interface FetchReportRunChartDataArgs {
  reportRunId: string;
  type: VisualizationType;
  interval: INTERVAL;
}

interface FetchReportRunChartDataResponse {
  reportColumns: AMCReportColumns[];
  chartData: AMCCSVRows[];
}

interface FetchReportRunDownloadUrlArgs {
  reportRunId: string;
}

interface FetchReportRunDownloadUrlResponse {
  downloadUrl: string;
}

interface FetchCampaignGroupsArgs {
  mid: string;
}

interface FetchCampaignGroupsResponse {
  campaignGroups: PrunedCampaignGroup[];
}

interface FetchCampaignsArgs extends Partial<AMCAdditionalFieldFilter> {
  mid: string;
}

interface FetchCampaignsResponse {
  campaigns: Campaign[];
}

interface CreateCampaignGroupResponse {
  campaignGroup: PrunedCampaignGroup;
}

export interface CreateCampaignGroupRequest {
  mid: string;
  name: string;
  campaigns: {
    id: string;
    name: string;
  }[];
}

interface GetReportCommentaryArgs {
  mid: string;
  reportRunId: string;
  currentChoice?: number;
}

interface GetReportCommentaryResponse {
  commentary: string | null;
  index: number;
  commentaryPrompt: string;
}

interface GetReportRunsArgs {
  mid: string;
  scheduledReportId: string;
}

interface GetReportRunsResponse {
  reportRuns: PrunedAMCScheduledReportRun[];
}

interface GetLatestReportRunArgs {
  mid: string;
}

interface GetLatestReportRunResponse {
  reportRun: PrunedAMCScheduledReportRun;
}

interface ValidateCustomQueryArgs {
  mid: string;
  query: string;
}

interface ValidateCustomQueryResponse {
  status: "success" | "error";
  errorMessage?: string;
}

interface CreateCustomAMCReportTypeArgs {
  mid: string;
  query: string;
  reportName: string;
  isPublic: boolean;
  description?: string;
  editReportId?: string | null;
}

interface CreateCustomAMCReportTypeResponse {
  status: "success" | "error";
  errorMessage?: string;
  reportId?: string;
}

interface DeleteCustomReportTypesArgs {
  reportId: string;
}

interface DeleteCustomReportTypesResponse {
  status: "success" | "error";
  errorMessage?: string;
}

interface UpdateCustomReportTypeData {
  name?: string;
  description?: string;
  public?: boolean;
  query?: string;
}

interface UpdateCustomReportTypesArgs {
  reportId: string;
  updateItem: UpdateCustomReportTypeData;
}
interface UpdateCustomReportTypesResponse {
  status: "success" | "error";
  errorMessage?: string;
}

interface TogglePublicReportTypeArgs {
  reportId: string;
  updateItem: {
    public: boolean;
  };
}

type TogglePublicReportTypeResponse = UpdateCustomReportTypesResponse;

interface GetChatResponseArgs {
  mid: string;
  prompt: string;
  forceGenerateSql?: boolean;
}

interface GetChatResponseResponse {
  message: string;
  instanceData: {
    name?: string;
    sql?: string;
    chatResponse?: string;
    isValid?: boolean;
    errorMessage?: string;
  };
}

const extendedApiSlice = api.injectEndpoints({
  endpoints: (build) => ({
    getReportTypes: build.query<FetchReportTypesResponse, FetchReportTypesArgs>(
      {
        query: (params) => {
          return {
            url: `${baseUrl}/api/amazon-marketing-cloud-service/api/reportType`,
            method: "GET",
            params,
          };
        },
        providesTags: [TAG_TYPES.AMCReportTypes],
        onQueryStarted: globalQueryErrorHandler("AMCReportTypes"),
      }
    ),
    createCustomReportType: build.mutation<
      CreateCustomAMCReportTypeResponse,
      CreateCustomAMCReportTypeArgs
    >({
      query: (params) => {
        return {
          url: `${baseUrl}/api/amazon-marketing-cloud-service/api/customReportType/createCustomReportType`,
          method: "POST",
          data: params,
        };
      },
      invalidatesTags: [TAG_TYPES.AMCReportTypes],
      onQueryStarted: globalQueryErrorHandler("CreateCustomAMCReportType"),
    }),
    deleteCustomReportType: build.mutation<
      DeleteCustomReportTypesResponse,
      DeleteCustomReportTypesArgs
    >({
      query: (params) => ({
        url: `${baseUrl}/api/amazon-marketing-cloud-service/api/customReportType/deleteCustomReportType`,
        method: "POST",
        data: params,
      }),
      onQueryStarted: globalQueryErrorHandler("DeleteAMCReportType"),
      invalidatesTags: [TAG_TYPES.AMCReportTypes],
    }),
    updateCustomReportType: build.mutation<
      UpdateCustomReportTypesResponse,
      UpdateCustomReportTypesArgs
    >({
      query: (params) => ({
        url: `${baseUrl}/api/amazon-marketing-cloud-service/api/customReportType/updateCustomReportType`,
        method: "POST",
        data: params,
      }),
      onQueryStarted: globalQueryErrorHandler("UpdateAMCReportType"),
      invalidatesTags: [TAG_TYPES.AMCReportTypes],
    }),
    // this also calls the updateCustomReportType endpoint
    // but it doesn't invalidate the cache
    togglePublicReportType: build.mutation<
      TogglePublicReportTypeResponse,
      TogglePublicReportTypeArgs
    >({
      query: (params) => ({
        url: `${baseUrl}/api/amazon-marketing-cloud-service/api/customReportType/updateCustomReportType`,
        method: "POST",
        data: params,
      }),
      onQueryStarted: globalQueryErrorHandler("TogglePublicAMCReportType"),
    }),
    scheduledReports: build.query<
      FetchScheduledReportsResponse,
      FetchScheduledReportsArgs
    >({
      query: (params) => {
        return {
          url: `${baseUrl}/api/amazon-marketing-cloud-service/api/scheduledReport`,
          method: "POST",
          data: params,
        };
      },
      providesTags: [TAG_TYPES.AMCScheduleReports],
      onQueryStarted: globalQueryErrorHandler("AMCScheduledReports"),
    }),
    createScheduledReport: build.mutation<
      CreateScheduledReportResponse,
      CreateScheduledReportArgs
    >({
      query: (params) => {
        const { successMsg, ...restParams } = params;
        return {
          url: `${baseUrl}/api/amazon-marketing-cloud-service/api/scheduledReport/create`,
          method: "POST",
          data: restParams,
        };
      },
      onQueryStarted: globalQueryErrorHandler(
        "AMCScheduledReports",
        false,
        (_data, dispatch, args: CreateScheduledReportArgs) => {
          setInfoMessage(dispatch, args.successMsg);
        }
      ),
    }),
    updateReportCustomPrompt: build.mutation<{}, UpdateReportCustomPromptArgs>({
      query: (params) => {
        return {
          url: `${baseUrl}/api/amazon-marketing-cloud-service/api/reportRun`,
          method: "PUT",
          data: params,
        };
      },
      onQueryStarted: globalQueryErrorHandler(
        "AMCUpdateReportCustomPrompt",
        false,
        (_data, dispatch, args: UpdateReportCustomPromptArgs) => {
          setInfoMessage(dispatch, args.successMsg);
        }
      ),
    }),
    reportRunData: build.query<
      AMCGenericReportTabularResponse,
      FetchReportRunArgs
    >({
      query: (params) => {
        return {
          url: `${baseUrl}/api/amazon-marketing-cloud-service/api/publicReportRun/${params.reportRunId}/tabular`,
          method: "POST",
          data: params,
        };
      },
      onQueryStarted: globalQueryErrorHandler("AMCReportRunData"),
    }),
    reportRunChartData: build.query<
      FetchReportRunChartDataResponse,
      FetchReportRunChartDataArgs
    >({
      query: ({ reportRunId, ...otherParams }) => {
        return {
          url: `${baseUrl}/api/amazon-marketing-cloud-service/api/publicReportRun/${reportRunId}/chartData`,
          method: "GET",
          params: otherParams,
        };
      },
      onQueryStarted: globalQueryErrorHandler("AMCReportRunChart"),
    }),
    reportRunDownloadUrl: build.query<
      FetchReportRunDownloadUrlResponse,
      FetchReportRunDownloadUrlArgs
    >({
      query: (params) => {
        return {
          url: `${baseUrl}/api/amazon-marketing-cloud-service/api/reportRun/${params.reportRunId}/downloadUrl`,
          method: "GET",
          params,
        };
      },
    }),
    getCampaigns: build.query<FetchCampaignsResponse, FetchCampaignsArgs>({
      query: (params) => {
        return {
          url: `${baseUrl}/api/amazon-marketing-cloud-service/api/campaigns`,
          method: "GET",
          params,
        };
      },
      onQueryStarted: globalQueryErrorHandler("AMCCampaigns"),
    }),
    getCampaignGroups: build.query<
      FetchCampaignGroupsResponse,
      FetchCampaignGroupsArgs
    >({
      query: (params) => {
        return {
          url: `${baseUrl}/api/amazon-marketing-cloud-service/api/campaignGroups`,
          method: "GET",
          params,
        };
      },
      providesTags: [TAG_TYPES.CampaignGroups],
      onQueryStarted: globalQueryErrorHandler("AMCCampaignGroups"),
    }),
    createCampaignGroup: build.mutation<
      CreateCampaignGroupResponse,
      CreateCampaignGroupRequest
    >({
      query: (params) => {
        return {
          url: `${baseUrl}/api/amazon-marketing-cloud-service/api/campaignGroups`,
          method: "POST",
          data: params,
        };
      },
      invalidatesTags: [TAG_TYPES.CampaignGroups],
      onQueryStarted: globalQueryErrorHandler("AMCCreateCampaignGroup"),
    }),
    getReportCommentary: build.mutation<
      GetReportCommentaryResponse,
      GetReportCommentaryArgs
    >({
      query: (params) => {
        return {
          url: `${baseUrl}/api/amazon-marketing-cloud-service/api/commentary/${params.reportRunId}`,
          method: "GET",
          params,
        };
      },
    }),
    getReportRuns: build.query<GetReportRunsResponse, GetReportRunsArgs>({
      query: (params) => ({
        url: `${baseUrl}/api/amazon-marketing-cloud-service/api/reportRun`,
        method: "GET",
        params,
      }),
      onQueryStarted: globalQueryErrorHandler("AMCReportRuns"),
    }),
    deleteAMCScheduledReport: build.mutation<{}, string>({
      query: (id) => ({
        url: `${baseUrl}/api/amazon-marketing-cloud-service/api/scheduledReport/${id}`,
        method: "DELETE",
      }),
      onQueryStarted: globalQueryErrorHandler("AMCDeleteScheduledReport"),
      invalidatesTags: [TAG_TYPES.AMCScheduleReports],
    }),
    getLatestReportRun: build.query<
      GetLatestReportRunResponse,
      GetLatestReportRunArgs
    >({
      query: (params) => ({
        url: `${baseUrl}/api/amazon-marketing-cloud-service/api/reportRun/latest`,
        method: "GET",
        params,
      }),
      onQueryStarted: globalQueryErrorHandler("AMCLatestReportRun"),
    }),
    validateCustomQuery: build.mutation<
      ValidateCustomQueryResponse,
      ValidateCustomQueryArgs
    >({
      query: (params) => ({
        url: `${baseUrl}/api/amazon-marketing-cloud-service/api/customReportType/validate`,
        method: "POST",
        data: params,
      }),
      onQueryStarted: globalQueryErrorHandler("AMCValidateCustomQuery"),
    }),
    getChatResponse: build.mutation<
      GetChatResponseResponse,
      GetChatResponseArgs
    >({
      query: (params) => ({
        url: `${baseUrl}/api/amazon-marketing-cloud-service/api/chatbot`,
        method: "POST",
        data: params,
      }),
      onQueryStarted: globalQueryErrorHandler("GetChatResponse"),
    }),
  }),
});

export const {
  useGetReportTypesQuery,
  useCreateCustomReportTypeMutation,
  useDeleteCustomReportTypeMutation,
  useUpdateCustomReportTypeMutation,
  useTogglePublicReportTypeMutation,
  useScheduledReportsQuery,
  useCreateScheduledReportMutation,
  useUpdateReportCustomPromptMutation,
  useReportRunDataQuery,
  useReportRunChartDataQuery,
  useReportRunDownloadUrlQuery,
  useGetCampaignGroupsQuery,
  useGetCampaignsQuery,
  useCreateCampaignGroupMutation,
  useGetReportCommentaryMutation,
  useGetReportRunsQuery,
  useDeleteAMCScheduledReportMutation,
  useGetLatestReportRunQuery,
  useValidateCustomQueryMutation,
  useGetChatResponseMutation,
} = extendedApiSlice;
